import { eFinSuiteAdminMessages } from 'src/constants/AppConstants';
import * as Yup from 'yup';

export const APP_NAME_MAX_LENGTH = 30;
export const SHORT_DESCRIPTION_MAX_LENGTH = 60;
export const FULL_DESCRIPTION_MAX_LENGTH = 600;

export const WELCOME_TITLE_MAX_LENGTH = 21;
export const WELCOME_LINK_DISPLAY_NAME_MAX_LENGTH = 46;
const WELCOME_LINK_DESCRIPTION_MAX_LENGTH = 1000;

export const OPTIONAL_MULTI_SELECT_DROPDOWN = Yup.array().of(
  Yup.object().shape({
    label: Yup.string(),
    id: Yup.string()
  })
);

export const REQUIRED_MULTI_SELECT_DROPDOWN = Yup.array()
  .of(
    Yup.object().shape({
      label: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
      id: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD)
    })
  )
  .min(1, eFinSuiteAdminMessages.AT_LEAST_ONE_SELECTION)
  .required(eFinSuiteAdminMessages.REQUIRED_FIELD);

export const REQUIRED_SELECT_DROPDOWN = Yup.object().shape({
  label: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
  id: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD)
});

const isValidUrl = (value: string | undefined): boolean => {
  if (!value) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

export const nonProductionEnvironmentSchema = Yup.object().shape({
  id: Yup.mixed().nullable(),
  displayText: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
  link: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD).test('valid-url', eFinSuiteAdminMessages.VALID_URL, isValidUrl)
});

export const shortDescriptionRegexPattern = new RegExp(`^(?=.{1,${SHORT_DESCRIPTION_MAX_LENGTH}}$).*$`);
export const SHORT_DESCRIPTION_ERROR_MESSAGE = `Max ${SHORT_DESCRIPTION_MAX_LENGTH} characters`;

export const logoConfigSchema = Yup.object()
  .nullable()
  .default(null)
  .shape({
    logoType: Yup.string().oneOf(['bundled', 'shared-library', 'app-specific']).required(),
    selectedLogoName: Yup.string().nullable(),
    uploadedFileName: Yup.string()
      .nullable()
      .when('logoType', {
        is: 'app-specific',
        then: Yup.string().nullable().required('Upload a logo file.'),
        otherwise: Yup.string().nullable()
      }),
    s3Location: Yup.object()
      .nullable()
      .when('logoType', {
        is: 'app-specific',
        then: Yup.object().nullable().required('Logo upload failed or is still in progress.').shape({
          bucket: Yup.string().required(),
          key: Yup.string().required(),
          region: Yup.string().required(),
          contentType: Yup.string().required()
        }),
        otherwise: Yup.object().nullable()
      })
  });

export const applicationDetailsSchema = (existingApplicationNames: string[], shouldSkipAppNameUniquenessValidation: boolean) => {
  return Yup.object().shape({
    applicationName: Yup.string()
      .required(eFinSuiteAdminMessages.REQUIRED_FIELD)
      .max(APP_NAME_MAX_LENGTH, `Max ${APP_NAME_MAX_LENGTH} characters`)
      .test('unique-application-name', eFinSuiteAdminMessages.UNIQUE_APP_NAME_ERROR, async function (value) {
        if (shouldSkipAppNameUniquenessValidation) {
          return true;
        }
        if (existingApplicationNames && existingApplicationNames.includes(value || '')) {
          return false;
        }
        return true;
      }),
    applicationLink: Yup.string().url(eFinSuiteAdminMessages.VALID_URL),
    applicationOwnerLabelInput: REQUIRED_SELECT_DROPDOWN,
    shortApplicationDescription: Yup.string()
      .required(eFinSuiteAdminMessages.REQUIRED_FIELD)
      .matches(shortDescriptionRegexPattern, `Max ${SHORT_DESCRIPTION_MAX_LENGTH} characters`),
    fullApplicationDescription: Yup.string()
      .required(eFinSuiteAdminMessages.REQUIRED_FIELD)
      .max(FULL_DESCRIPTION_MAX_LENGTH, `Max ${FULL_DESCRIPTION_MAX_LENGTH} characters`),
    glAccounts: REQUIRED_SELECT_DROPDOWN,
    toolUI: REQUIRED_SELECT_DROPDOWN,
    qChatConfig: Yup.object()
      .nullable()
      .default(null)
      .shape({
        agentUrl: Yup.string()
          .transform((value, originalValue) => (originalValue === '' ? null : value))
          .nullable()
          .when('isEnabled', {
            is: true,
            then: Yup.string()
              .transform((value, originalValue) => (originalValue === '' ? null : value))
              .nullable()
              .required('Agent URL is required when Q Chat is enabled.')
              .url(eFinSuiteAdminMessages.VALID_URL),
            otherwise: Yup.string()
              .transform((value, originalValue) => (originalValue === '' ? null : value))
              .nullable()
              .url(eFinSuiteAdminMessages.VALID_URL)
          }),
        isEnabled: Yup.boolean().nullable()
      }),
    logoConfig: logoConfigSchema
  });
};

export const reportIssuesAndAdditionalDetailsSchema = Yup.object().shape({
  id: Yup.mixed().nullable(),
  position: Yup.number()
    .typeError(eFinSuiteAdminMessages.ENTER_VALID_NUMBER)
    .nullable()
    .min(1, 'Value must be between 1 and 8')
    .max(8, 'Value must be between 1 and 8'),
  displayName: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
  link: Yup.string().url(eFinSuiteAdminMessages.VALID_URL).required(eFinSuiteAdminMessages.REQUIRED_FIELD)
});

export const welcomeContentDetailsSchemaForApplications = Yup.object().shape({
  id: Yup.mixed().nullable(),
  displayText: Yup.string()
    .nullable()
    .required(eFinSuiteAdminMessages.REQUIRED_FIELD)
    .max(WELCOME_LINK_DISPLAY_NAME_MAX_LENGTH, `Max ${WELCOME_LINK_DISPLAY_NAME_MAX_LENGTH} characters`),
  link: Yup.string().nullable().url(eFinSuiteAdminMessages.VALID_URL),
  description: Yup.string().nullable().max(WELCOME_LINK_DESCRIPTION_MAX_LENGTH, `Max ${WELCOME_LINK_DESCRIPTION_MAX_LENGTH} characters`),
  pointOfContact: Yup.string()
    .nullable()
    .test(
      'pointOfContact-validation',
      'Use only alphabets, space, and one comma. Examples: "useralias" or "useralias, anotheralias"',
      function (value) {
        if (value === null || value === undefined || value === '') {
          return true;
        }
        return /^[a-zA-Z]+(\s*,\s*[a-zA-Z]+)?$/.test(value);
      }
    )
});

export const linksAndTagsSchema = Yup.object().shape({
  hasNonProductionEnvironments: Yup.boolean().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
  nonProductionEnvironments: Yup.array().when('hasNonProductionEnvironments', {
    is: true,
    then: Yup.array()
      .of(nonProductionEnvironmentSchema)
      .min(1, eFinSuiteAdminMessages.AT_LEAST_ONE_RECORD)
      .required(eFinSuiteAdminMessages.REQUIRED_FIELD),
    otherwise: Yup.array(nonProductionEnvironmentSchema)
  }),
  timeGranularity: OPTIONAL_MULTI_SELECT_DROPDOWN,
  keyDimension: OPTIONAL_MULTI_SELECT_DROPDOWN,
  lastEnhanced: OPTIONAL_MULTI_SELECT_DROPDOWN,
  process: OPTIONAL_MULTI_SELECT_DROPDOWN,
  scenario: OPTIONAL_MULTI_SELECT_DROPDOWN
});

export const ApplicationFormSchema = (applicationNames: string[], shouldSkipAppNameUniquenessValidation: boolean) =>
  Yup.object().shape({
    id: Yup.mixed().nullable(),
    applicationDetails: applicationDetailsSchema(applicationNames, shouldSkipAppNameUniquenessValidation),
    linksAndTags: linksAndTagsSchema
  });
